import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Sparkles, SlidersHorizontal, History, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { employeesAPI } from '../../lib/api';
import { CompetencyRuler, bandOf } from './CompetencyRuler';
import { CompetencyAssessmentDialog } from './CompetencyAssessmentDialog';

const fmt = (d) => (d ? new Date(d).toLocaleDateString('pt-BR') : '-');

const CompetencyItem = ({ it, idx }) => {
  const band = bandOf(it.nota);
  return (
    <div className="bg-white/80 rounded-lg border border-white px-3 py-2" data-testid={`competency-item-${idx}`}>
      <div className="flex items-center justify-between gap-2 mb-1">
        <p className="text-sm text-slate-800">{it.competencia}</p>
        <div className="flex items-center gap-2 shrink-0">
          {band && <span className={`text-[11px] px-2 py-0.5 rounded-full ${band.chip}`}>{band.label}</span>}
          {it.status && (
            <Badge className={it.status === 'atinge' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'} data-testid={`competency-status-${idx}`}>
              {it.status === 'atinge' ? 'Atinge' : `Abaixo da meta (−${it.lacuna})`}
            </Badge>
          )}
          <span className="text-base font-bold w-12 text-right" data-testid={`competency-nota-${idx}`}>{it.nota ?? '—'}</span>
        </div>
      </div>
      <CompetencyRuler nota={it.nota} meta={it.meta} />
      {it.avaliado_em && (
        <p className="text-[11px] text-slate-500">
          Avaliado em {fmt(it.avaliado_em)}{it.avaliador_nome ? ` por ${it.avaliador_nome}` : ''}{it.observacao ? ` · "${it.observacao}"` : ''}
        </p>
      )}
    </div>
  );
};

const HistoryList = ({ historico }) => (
  <div className="mt-3 space-y-2" data-testid="competency-history">
    {historico.length === 0 && <p className="text-xs text-slate-500">Nenhuma avaliação registrada</p>}
    {historico.map((h) => (
      <div key={h.id} className="bg-white rounded-lg border p-2 text-xs">
        <p className="font-medium text-slate-700">{fmt(h.avaliado_em)} · média {h.media} · {h.avaliador_nome || '-'}</p>
        <ul className="text-slate-500 mt-0.5 space-y-0.5">
          {h.itens.map((i) => (
            <li key={i.competencia}>{i.competencia}: <strong>{i.nota}</strong>{i.observacao ? ` — "${i.observacao}"` : ''}</li>
          ))}
        </ul>
      </div>
    ))}
  </div>
);

export const CompetencySection = ({ employee }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const load = useCallback(() => {
    employeesAPI.getCompetencyAssessments(employee.id).then((r) => setData(r.data)).catch(() => setData(false));
  }, [employee.id]);
  useEffect(() => { setData(null); load(); }, [load]);

  if (data === null) return <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-teal-600" /></div>;
  if (data === false) return <p className="text-sm text-red-600">Erro ao carregar competências</p>;
  const mediaBand = bandOf(data.media);

  return (
    <div className="rounded-xl border p-4 border-teal-200 bg-teal-50/60" data-testid="qual-group-competencia">
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <Sparkles className="w-5 h-5 text-teal-800" />
        <h3 className="font-semibold text-teal-800">Habilidades e Competências</h3>
        <span className="text-xs text-slate-500">— régua de 0 a 100</span>
        <div className="ml-auto flex items-center gap-2">
          {data.media !== null && (
            <span className={`text-xs px-2 py-1 rounded-full ${mediaBand.chip}`} data-testid="competency-media">
              Média {data.media} · {mediaBand.label} · {data.atingem_meta}/{data.itens.length} atingem a meta
            </span>
          )}
          {data.itens.length > 0 && (
            <Button size="sm" className="bg-teal-600 hover:bg-teal-700" onClick={() => setOpen(true)} data-testid="competency-evaluate-btn">
              <SlidersHorizontal className="w-4 h-4 mr-1" /> Avaliar competências
            </Button>
          )}
        </div>
      </div>
      {data.itens.length === 0 ? (
        <p className="text-sm text-slate-500 italic mt-2">Nenhuma competência definida na descrição do cargo</p>
      ) : (
        <div className="space-y-2 mt-3">
          {data.itens.map((it, i) => <CompetencyItem key={it.competencia} it={it} idx={i} />)}
        </div>
      )}
      <button type="button" className="mt-3 text-xs text-teal-700 flex items-center gap-1" onClick={() => setShowHistory((v) => !v)} data-testid="competency-history-toggle">
        <History className="w-3.5 h-3.5" /> Histórico ({data.historico.length})
        {showHistory ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
      </button>
      {showHistory && <HistoryList historico={data.historico} />}
      <CompetencyAssessmentDialog open={open} onClose={() => setOpen(false)} employee={employee} data={data} onSaved={load} />
    </div>
  );
};
